import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle, Loader2, Coins, ArrowRight } from "lucide-react";

export default function PaymentSuccessPage() {
  const { api, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading");
  const [credits, setCredits] = useState(0);
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get("session_id");

  const pollStatus = useCallback(async () => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    try {
      const res = await api.get(`/payments/checkout/status/${sessionId}`);
      const data = res.data;

      if (data.payment_status === "paid") {
        setStatus("success");
        setCredits(data.credits);
        refreshUser();
      } else if (data.status === "expired" || data.payment_status === "expired") {
        setStatus("expired");
      } else {
        // Still pending, retry
        if (attempts < 10) {
          setAttempts((a) => a + 1);
          setTimeout(() => pollStatus(), 2000);
        } else {
          setStatus("timeout");
        }
      }
    } catch (err) {
      if (attempts < 5) {
        setAttempts((a) => a + 1);
        setTimeout(() => pollStatus(), 2000);
      } else {
        setStatus("error");
      }
    }
  }, [api, sessionId, attempts, refreshUser]);

  useEffect(() => {
    pollStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="py-12 flex items-center justify-center min-h-[60vh]" data-testid="payment-success-page">
      <div className="bg-card border border-border rounded-3xl p-8 max-w-sm w-full text-center shadow-sm">
        {status === "loading" && (
          <>
            <Loader2 className="w-12 h-12 text-[#FF6B35] mx-auto mb-4 animate-spin" />
            <h2 className="font-heading font-bold text-lg text-card-foreground mb-2">Verification du paiement...</h2>
            <p className="text-sm text-muted-foreground">Un instant, on verifie ta transaction</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-16 h-16 rounded-full bg-emerald-50 border-2 border-emerald-200 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-500" />
            </div>
            <h2 className="font-heading font-bold text-xl text-card-foreground mb-2">Paiement reussi !</h2>
            <div className="flex items-center justify-center gap-2 mb-4">
              <Coins className="w-5 h-5 text-[#FF6B35]" />
              <span className="font-mono-credits font-bold text-2xl text-[#FF6B35]">
                +{credits.toLocaleString()}
              </span>
              <span className="text-sm text-muted-foreground">credits</span>
            </div>
            <p className="text-sm text-card-foreground/80 mb-6">
              Tes credits ont ete ajoutes a ton compte. Mise maintenant sur tes artistes preferes !
            </p>
            <button
              onClick={() => navigate("/discover")}
              className="inline-flex items-center gap-2 bg-[#FF6B35] text-white font-bold px-6 py-3 rounded-full shadow-[0_0_15px_rgba(255,107,53,0.3)] hover:scale-105 active:scale-95 transition-transform"
              data-testid="success-discover-btn"
            >
              Explorer
              <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}

        {(status === "error" || status === "timeout" || status === "expired") && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-50 border-2 border-red-200 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="font-heading font-bold text-xl text-card-foreground mb-2">
              {status === "expired" ? "Session expiree" : "Erreur de verification"}
            </h2>
            <p className="text-sm text-card-foreground/80 mb-6">
              {status === "expired"
                ? "La session de paiement a expire. Aucun montant n'a ete debite."
                : "Impossible de verifier le paiement. Si tu as ete debite, contacte le support."}
            </p>
            <button
              onClick={() => navigate("/credits")}
              className="inline-flex items-center gap-2 bg-muted text-card-foreground font-bold px-6 py-3 rounded-full border border-border hover:bg-border transition-colors"
              data-testid="retry-btn"
            >
              Retour a la boutique
            </button>
          </>
        )}
      </div>
    </div>
  );
}
